/**
 * Composición de la aplicación: sesión, catálogos, ruta y guardas.
 *
 * El orden de las guardas no es arbitrario:
 *   1. sin sesión            → Acceso, y nada más se monta
 *   2. contraseña temporal   → Primer acceso, y nada más se monta
 *   3. ruta desconocida      → la vista inicial de esta persona
 *   4. ruta sin permiso      → la vista inicial de esta persona
 *
 * `SEG-1`: los pasos 3 y 4 **no autorizan**, solo evitan ofrecer una pantalla
 * que el servidor va a rechazar. La autorización vive en el backend.
 */
import { useCallback, useEffect } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { TooltipProvider } from '@mpfn/ui'
import { api, ErrorApi } from './api/cliente.js'
import { claves } from './api/claves.js'
import { ProveedorCatalogos, type Catalogos } from './api/catalogos.js'
import type { RespuestaSesion, UsuarioSesion } from './api/tipos.js'
import { ProveedorSesion } from './sesion/contexto.js'
import { Marco } from './componentes/Marco.js'
import { Avisos } from './componentes/Avisos.js'
import { navegarA } from './navegacion/historial.js'
import { useUbicacion } from './navegacion/useUbicacion.js'
import { construirRuta, resolverRuta, type Vista } from './navegacion/rutas.js'
import { rutaPermitida, vistaInicial } from './navegacion/permisos.js'
import { Acceso } from './vistas/Acceso.js'
import { PrimerAcceso } from './vistas/PrimerAcceso.js'
import { Panel } from './vistas/Panel.js'
import { Sorteo } from './vistas/Sorteo.js'
import { Bloque } from './vistas/Bloque.js'
import { Expediente } from './vistas/Expediente.js'
import { Documento } from './vistas/Documento.js'
import { ColaMesaPartes } from './vistas/ColaMesaPartes.js'
import { MisOficios } from './vistas/MisOficios.js'
import { DerivacionesOci } from './vistas/DerivacionesOci.js'
import { Entidades } from './vistas/Entidades.js'
import { Politica } from './vistas/Politica.js'
import { Calendario } from './vistas/Calendario.js'
import { Plantillas } from './vistas/Plantillas.js'
import { ItemsSeleccion } from './vistas/ItemsSeleccion.js'
import { Usuarios } from './vistas/Usuarios.js'
import { Auditoria } from './vistas/Auditoria.js'

function PantallaDeArranque({ mensaje }: { mensaje: string }) {
  return (
    // `<output>` es el elemento nativo con semántica de región en vivo: el
    // lector de pantalla anuncia el cambio de mensaje sin `role` explícito.
    <div className="sigfisc-arranque">
      <output>{mensaje}</output>
    </div>
  )
}

export function App() {
  const cliente = useQueryClient()
  const ubicacion = useUbicacion()

  const sesion = useQuery({
    queryKey: claves.sesion,
    queryFn: async () => (await api.obtener<RespuestaSesion>('/auth/yo')).usuario,
    // Un 401 es la respuesta normal de un visitante sin sesión, no un fallo
    // que merezca tres reintentos con espera exponencial.
    retry: (intentos, error) => !(error instanceof ErrorApi) && intentos < 2,
    staleTime: 60_000,
    // Un visitante no tiene datos de sesión: reconsultar al volver a la pestaña
    // pondría la consulta en pending y desmontaría el formulario de acceso.
    // Con una sesión activa sí se comprueba su vigencia al recuperar el foco.
    refetchOnWindowFocus: (query) => query.state.data !== undefined,
  })

  const usuario: UsuarioSesion | undefined = sesion.data

  // Los catálogos son constantes del despliegue: se piden una vez y no
  // caducan mientras dure la pestaña (`ARQ-8`).
  const catalogos = useQuery({
    queryKey: claves.catalogos,
    queryFn: () => api.obtener<Catalogos>('/catalogos'),
    enabled: Boolean(usuario) && !usuario?.requiereCambioPassword,
    staleTime: Infinity,
    gcTime: Infinity,
  })

  const refrescar = useCallback(async () => {
    await cliente.invalidateQueries({ queryKey: claves.sesion })
  }, [cliente])

  const cerrarSesion = useCallback(async () => {
    try {
      await api.eliminar('/auth/sesion')
    } finally {
      // Se limpia TODA la caché: los datos del usuario anterior no pueden
      // quedar disponibles para quien inicie sesión a continuación.
      cliente.clear()
      navegarA(construirRuta('acceso'), { reemplazar: true })
    }
  }, [cliente])

  const resuelta = resolverRuta(ubicacion.pathname)
  const vista: Vista | undefined = resuelta?.ruta.vista

  // ── Guardas de navegación ─────────────────────────────────────────────────
  // En un efecto y no durante el render: `navegarA` escribe en el historial,
  // que es un efecto secundario y no puede ocurrir en el cuerpo del componente.
  useEffect(() => {
    if (sesion.isPending) return

    if (!usuario) {
      if (vista !== 'acceso') navegarA(construirRuta('acceso'), { reemplazar: true })
      return
    }

    if (usuario.requiereCambioPassword) {
      if (vista !== 'primerAcceso') navegarA(construirRuta('primerAcceso'), { reemplazar: true })
      return
    }

    // Ya autenticado y con contraseña propia: las dos pantallas de acceso
    // dejan de tener sentido.
    if (vista === 'acceso' || vista === 'primerAcceso' || !resuelta) {
      navegarA(construirRuta(vistaInicial(usuario)), { reemplazar: true })
      return
    }

    if (!rutaPermitida(usuario, resuelta.ruta)) {
      navegarA(construirRuta(vistaInicial(usuario)), { reemplazar: true })
    }
  }, [sesion.isPending, usuario, vista, resuelta])

  if (sesion.isPending) return <PantallaDeArranque mensaje="Comprobando la sesión…" />

  if (!usuario) {
    return (
      <>
        <Acceso onIniciada={refrescar} />
        <Avisos />
      </>
    )
  }

  if (usuario.requiereCambioPassword) {
    return (
      <>
        <PrimerAcceso nombre={usuario.nombre} onCambiada={refrescar} />
        <Avisos />
      </>
    )
  }

  if (catalogos.isPending) return <PantallaDeArranque mensaje="Cargando el sistema…" />
  if (catalogos.isError || !catalogos.data) {
    return <PantallaDeArranque mensaje="No se pudo cargar la configuración del sistema. Recargue la página." />
  }

  // Mientras la guarda redirige, no se monta una vista que no corresponde.
  if (!resuelta || !vista || vista === 'acceso' || vista === 'primerAcceso') {
    return <PantallaDeArranque mensaje="Abriendo…" />
  }
  if (!rutaPermitida(usuario, resuelta.ruta)) {
    return <PantallaDeArranque mensaje="Abriendo…" />
  }

  return (
    <ProveedorSesion value={{ usuario, cerrarSesion, refrescar }}>
      <ProveedorCatalogos value={catalogos.data}>
        <TooltipProvider>
          <Marco vistaActiva={vista}>
            <VistaActual vista={vista} parametros={resuelta.parametros} />
          </Marco>
          <Avisos />
        </TooltipProvider>
      </ProveedorCatalogos>
    </ProveedorSesion>
  )
}

function VistaActual({
  vista,
  parametros,
}: {
  vista: Vista
  parametros: Readonly<Record<string, string>>
}) {
  switch (vista) {
    case 'panel':
      return <Panel />
    case 'sorteo':
      return <Sorteo />
    case 'bloque':
      return <Bloque periodo={parametros['periodo'] ?? ''} />
    case 'expediente':
      return <Expediente id={parametros['id'] ?? ''} />
    case 'documento':
      return <Documento id={parametros['id'] ?? ''} />
    case 'mesaPartes':
      return <ColaMesaPartes />
    case 'misOficios':
      return <MisOficios />
    case 'oci':
      return <DerivacionesOci />
    case 'entidades':
      return <Entidades />
    case 'politica':
      return <Politica />
    case 'calendario':
      return <Calendario />
    case 'plantillas':
      return <Plantillas />
    case 'itemsSeleccion':
      return <ItemsSeleccion />
    case 'usuarios':
      return <Usuarios />
    case 'auditoria':
      return <Auditoria />
    case 'acceso':
    case 'primerAcceso':
      return null
  }
}
